
import React, { useState, useEffect } from 'react';
import { X, Check, AlertCircle, Info } from 'lucide-react';

// --- TOAST SYSTEM ---
type ToastType = 'success' | 'error' | 'info';
type ToastEvent = { message: string; type: ToastType; id: number };
const listeners: ((toast: ToastEvent) => void)[] = [];

export const notify = (message: string, type: ToastType = 'success') => {
  const event = { message, type, id: Date.now() };
  listeners.forEach(l => l(event));
};

export const ToastContainer = () => {
  const [toasts, setToasts] = useState<ToastEvent[]>([]);

  useEffect(() => {
    const handler = (t: ToastEvent) => {
      setToasts(prev => [...prev, t]);
      setTimeout(() => {
        setToasts(prev => prev.filter(x => x.id !== t.id));
      }, 3000);
    };
    listeners.push(handler);
    return () => {
      const idx = listeners.indexOf(handler);
      if (idx > -1) listeners.splice(idx, 1);
    };
  }, []);

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col space-y-3 pointer-events-none">
      {toasts.map(t => (
        <div key={t.id} className={`pointer-events-auto px-5 py-4 cut-corner border-2 ${t.type === 'success' ? 'border-emerald-500' : t.type === 'error' ? 'border-red-600' : 'border-[var(--accent)]'} shadow-[0_0_20px_rgba(0,0,0,0.9)] animate-slash-in text-white text-base font-display flex items-center space-x-4 bg-[var(--bg-card)] uppercase tracking-widest`}>
          {t.type === 'success' && <Check size={24} className="text-emerald-500 drop-shadow-[0_0_8px_rgba(16,185,129,0.8)]" />}
          {t.type === 'error' && <AlertCircle size={24} className="text-red-600 drop-shadow-[0_0_8px_rgba(239,68,68,0.8)] animate-pulse" />}
          {t.type === 'info' && <Info size={24} className="text-[var(--accent)] drop-shadow-[0_0_8px_var(--accent-glow)]" />}
          <span className="mt-1">{t.message}</span>
        </div>
      ))}
    </div>
  );
};

// --- COMPONENTS ---

export const Button = ({ children, onClick, variant = 'primary', size = 'md', className = '', type = 'button', disabled = false, ...props }: any) => {
  const base = "font-display font-bold transition-all duration-300 flex items-center justify-center space-x-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border-2 outline-none focus:ring-0 uppercase tracking-widest cut-corner";

  const sizes = {
    sm: "text-sm py-1.5 px-5",
    md: "text-lg py-2 px-8",
    lg: "text-xl py-3 px-10"
  };

  const variants = {
    primary: "btn-rock bg-[#000] text-[var(--accent)] border-[var(--accent)] hover:bg-[var(--accent)] hover:text-[#000] hover:shadow-[0_0_25px_var(--accent-glow)]",
    secondary: "bg-[var(--bg-card)] text-[var(--text-primary)] border-[var(--border-color)] hover:border-[var(--text-primary)] hover:bg-[#1a1a1c]",
    danger: "bg-[#050000] text-red-500 border-red-500 hover:text-white caution-stripe hover:shadow-[0_0_25px_rgba(239,68,68,0.5)] hover:bg-red-900 border-dashed",
    ghost: "text-[var(--text-secondary)] border-transparent hover:text-[var(--accent)] hover:border-[var(--border-subtle)] bg-transparent",
    success: "bg-emerald-950 text-emerald-400 border-emerald-500 hover:bg-emerald-500 hover:text-[#000] hover:shadow-[0_0_25px_rgba(16,185,129,0.5)]"
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${sizes[size as keyof typeof sizes]} ${variants[variant as keyof typeof variants]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const Input = ({ label, className = "", error, ...props }: any) => (
  <div className={`flex flex-col space-y-2 w-full ${className}`}>
    {label && <label className="text-sm font-display text-[var(--text-secondary)] tracking-[0.15em] uppercase">{label}</label>}
    <input
      className={`px-4 py-3 border-2 cut-corner-reverse ${error ? 'border-red-600 focus:border-red-500 shadow-[inset_0_0_10px_rgba(239,68,68,0.2)]' : 'border-[var(--border-color)] hover:border-[var(--text-muted)] focus:border-[var(--accent)] focus:shadow-[inset_0_0_10px_var(--accent-soft)]'} bg-[#0a0a0c] text-[var(--text-primary)] font-mono placeholder-[var(--text-muted)] outline-none transition-all text-sm disabled:bg-[#030303] disabled:text-[var(--border-color)]`}
      {...props}
    />
    {error && <span className="text-xs font-bold font-display text-red-500 flex items-center mt-1 uppercase tracking-widest"><AlertCircle size={14} className="mr-1.5" />{error}</span>}
  </div>
);

export const Select = ({ label, className = "", error, children, ...props }: any) => (
  <div className={`flex flex-col space-y-2 w-full ${className}`}>
    {label && <label className="text-sm font-display text-[var(--text-secondary)] tracking-[0.15em] uppercase">{label}</label>}
    <div className="relative">
      <select
        className={`w-full px-4 py-3 border-2 cut-corner-reverse ${error ? 'border-red-600 focus:border-red-500 shadow-[inset_0_0_10px_rgba(239,68,68,0.2)]' : 'border-[var(--border-color)] hover:border-[var(--text-muted)] focus:border-[var(--accent)] focus:shadow-[inset_0_0_10px_var(--accent-soft)]'} bg-[#0a0a0c] text-[var(--text-primary)] font-mono outline-none transition-all text-sm appearance-none disabled:bg-[#030303] disabled:text-[var(--border-color)] cursor-pointer`}
        {...props}
      >
        {children}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-[var(--accent)]">
        <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
      </div>
    </div>
    {error && <span className="text-xs font-bold font-display text-red-500 flex items-center mt-1 uppercase tracking-widest"><AlertCircle size={14} className="mr-1.5" />{error}</span>}
  </div>
);

export const Modal = ({ isOpen, onClose, title, children, maxWidth = 'max-w-xl' }: any) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/90 backdrop-blur-xl transition-opacity animate-ignition" onClick={onClose}></div>
      <div className={`bg-[var(--bg-card)] cut-corner shadow-[0_0_50px_rgba(245,158,11,0.15)] w-full ${maxWidth} relative z-10 flex flex-col max-h-[90vh] border-2 border-[var(--accent)] animate-slash-in`}>
        <div className="flex justify-between items-center px-6 py-5 border-b-2 border-[var(--accent)]/50 bg-[#000] caution-stripe relative overflow-hidden">
          <div className="absolute inset-0 bg-black/80 z-0"></div>
          <h3 className="text-2xl font-display text-[var(--accent)] tracking-[0.2em] relative z-10 drop-shadow-[0_0_5px_var(--accent-glow)]">{title}</h3>
          <button onClick={onClose} className="text-[var(--accent)] hover:text-[#000] hover:bg-[var(--accent)] p-1.5 transition-colors relative z-10 cut-corner border border-[var(--accent)]"><X size={24} /></button>
        </div>
        <div className="p-8 overflow-y-auto bg-mesh relative">
          {children}
        </div>
        <div className="h-1.5 w-full bg-[var(--accent)] shadow-[0_0_10px_var(--accent-glow)]"></div>
      </div>
    </div>
  );
};

export const Badge = ({ color = 'gray', children }: any) => {
  const colors = {
    gray: 'bg-[#111] text-[var(--text-secondary)] border-2 border-[var(--border-color)]',
    green: 'bg-[#000] text-emerald-400 border-2 border-emerald-500',
    red: 'bg-[#000] text-red-500 border-2 border-red-500 caution-stripe relative',
    blue: 'bg-[#000] text-[var(--accent)] border-2 border-[var(--accent)]',
    yellow: 'bg-[#000] text-amber-400 border-2 border-amber-500',
    orange: 'bg-[#000] text-[#f97316] border-2 border-[#f97316]',
    purple: 'bg-[#000] text-purple-400 border-2 border-purple-500'
  };
  return (
    <span className={`px-4 py-1.5 text-sm font-display uppercase tracking-widest cut-corner ${colors[color as keyof typeof colors] || colors.gray}`}>
      {/* For red caution badge */}
      {color === 'red' ? <span className="absolute inset-0 bg-black/60 z-0"></span> : null}
      <span className="relative z-10">{children}</span>
    </span>
  );
}

export const Card = ({ children, className = '', noHover = false, variant = 'default', ...props }: any) => {
  const variants = {
    default: "border-[var(--border-color)]",
    blue: "border-[var(--accent)] overflow-visible",
    green: "border-emerald-500/50",
    red: "border-red-500/80 caution-stripe relative",
    orange: "border-orange-500/50",
  };

  const borderClass = variants[variant as keyof typeof variants] || variants.default;

  return (
    <div
      className={`bg-[var(--bg-card)] border-2 cut-corner ${borderClass} shadow-2xl ${!noHover ? 'hover:border-[var(--accent)] hover:-translate-y-1.5 hover:shadow-[0_15px_40px_rgba(0,0,0,0.9)]' : ''} transition-all duration-300 relative ${className}`}
      {...props}
    >
      <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[var(--accent)] to-transparent opacity-20"></div>
      {variant === 'red' && <div className="absolute inset-0 bg-black/80 z-0 pointer-events-none"></div>}
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
};
